import React, { createContext, useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { authService } from '../services/authService';
import { userAccessDirectory } from '../features/auth/UserAccess.jsx';

export const AuthContext = createContext();

export const AuthProvider = props => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [loginError, setLoginError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userType, setUserType] = useState(null);
    const [distributor, setDistributor] = useState(null);

    useEffect(() => {
        //init auth
        var authToken = localStorage.getItem('authToken');
        if (authToken) {
            init_auth(authToken);
        } else {
            setLoading(false)
        }
    }, []);

    const init_auth = async authToken => {
        const userResponse = await authService.get_user();
        if (userResponse) {
            if (userResponse.data) {
                if (userResponse.data.refreshed_token) {
                    localStorage.setItem('authToken', userResponse.data.refreshed_token);
                }
                if (userResponse.data.user) {
                    const user = userResponse.data.user;
                    setUsername(user.email);
                    setEmail(user.email);
                    setLoginError(null);
                    setUserType(user.type);
                    setDistributor(user.distributor);
                    setIsLoggedIn(true);
                }
                if(userResponse.data.pathianDataApiKey) {
                    localStorage.setItem('pathianDataApiKey', userResponse.data.pathianDataApiKey);
                }
            }
        }
        setLoading(false);
    };

    const login = useCallback(async (email, password) => {
        try {
            setLoading(true);
            setLoginError(null);
            const loginResponse = await authService.login(email, password);
            if (loginResponse?.error) {
                setLoginError(loginResponse?.message);
            } else {
                //setIsLoggedIn(true);
                setUsername(loginResponse.username);
                setEmail(loginResponse.username);
                localStorage.setItem('authToken', loginResponse.token);
                await init_auth(loginResponse.token);
            }
        } catch (err) {
            setLoginError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = () => {
        setIsLoggedIn(false);
        setUsername('');
        localStorage.removeItem('authToken');
    };

    const hasDistributor = useCallback(() => {
        return distributor !== null;
    }, [distributor]);

    const isSystemAdmin = useCallback(() => {
        return userType === 'System Administrator';
    }, [userType]);

    const isDistributorAdmin = useCallback(() => {
        return userType === 'Distributor Administrator';
    }, [userType]);

    const isPathianTech = useCallback(() => {
        return userType === 'Pathian Technician';
    }, [userType]);

    const isAccountExecutive = useCallback(() => {
        return userType === 'Account Executive';
    }, [userType]);

    const getUserType = useCallback(() => {
        return userType;
    }, [userType]);

    const doesUserHaveAccess = (userType, page) => {
        return userAccessDirectory[userType]?.includes(page);
    };

    const checkAuth = useCallback((userType, page) => {
        if(!userAccessDirectory[userType]?.includes(page)){
            console.log("User does not have access to this page: " + page);
            logout();
            return true;
        }
        else {return false;}
    }, []);

    const values = useMemo(
        () => ({
            username,
            setUsername,
            email,
            setEmail,
            password,
            setPassword,
            loginError,
            setLoginError,
            loading,
            setLoading,
            isLoggedIn,
            setIsLoggedIn,
            login,
            logout,
            checkAuth,
            userType,
            isSystemAdmin,
            isPathianTech,
            isDistributorAdmin,
            isAccountExecutive,
            getUserType,
            doesUserHaveAccess,
            distributor,
            setDistributor,
            hasDistributor,
        }),
        [
            username,
            email,
            isLoggedIn,
            loading,
            loginError,
            password,
            userType,
            isAccountExecutive,
            getUserType,
            isDistributorAdmin,
            isPathianTech,
            isSystemAdmin,
            distributor,
            setDistributor,
            hasDistributor,
            login,
            checkAuth
        ]
    );

    return <AuthContext.Provider value={values}>{props.children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within a AuthProvider component.');
    }

    return context;
};
